"use client";
import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import Image from "next/image";

import {
  FaShieldAlt,
  FaHandHoldingUsd,
  FaBalanceScale,
  FaChartLine,
} from "react-icons/fa";
import HeroSection from "./herosection";
import { MurabahahHeroSection } from "../MurabahahHeroSection";

// Clean color palette - Only Deep Teal Green and White
const colors = {
  primary: "#395750",      // Deep Teal Green - Company color
  primaryDark: "#2A423C",  // Darker teal
  primaryLight: "#4B6B63", // Lighter teal
  white: "#FFFFFF",        // White
  whiteOff: "#F8F9FA",     // Off-white for subtle contrast
  textDark: "#1A2E29",     // Dark text for contrast
};

const services = [
  {
    title: "Murabaha Financing",
    image: "/projects/profile.jpg",
    description:
      "A Sharia-compliant financing method where goods are sold at a disclosed profit margin, providing transparency and fairness for businesses and individuals.",
    lists: [
      "Cost-plus sale with known profit",
      "Asset-backed financing structure",
      "Flexible payment terms",
      "Transparent pricing agreements"
    ],
    details:
      "Murabaha offers an ethical alternative to interest-based lending by enabling customers to acquire assets through a cost-plus arrangement. It ensures transparency in pricing and supports responsible financial planning.",
  },
  {
    title: "Ijarah Financing",
    image: "/projects/profile2.jpg",
    description:
      "A lease-based Sharia-compliant financing structure where assets are rented for a fixed term, promoting accessibility without ownership burdens.",
    lists: [
      "Fixed-term lease agreements",
      "Asset usage without ownership",
      "Flexible rental terms",
      "End-of-term purchase options"
    ],
    details:
      "Ijarah enables clients to utilize assets through leasing, ensuring Sharia compliance by avoiding interest-based transactions. It offers structured rentals, transparent terms, and optional ownership at the end of the lease period.",
  },
];

const circles = Array.from({ length: 8 }, (_, i) => ({
  id: i,
  x: Math.random() * 100,
  y: Math.random() * 100,
  delay: Math.random() * 3,
}));

const text = "Explore our loan products & financing solutions";

const backgroundVariants = {
  rest: { x: 0, y: 0 },
  hover: { x: [0, 15, -15, 0], y: [0, -15, 15, 0] },
};

const rectTopLeftVariants = {
  rest: { x: 0, y: 0 },
  hover: { x: -10, y: -10 },
};

const rectBottomRightVariants = {
  rest: { x: 0, y: 0 },
  hover: { x: 10, y: 10 },
};

const AboutLanaIF = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (currentIndex < text.length) {
      const timeout = setTimeout(() => {
        setCurrentIndex((prev) => prev + 1);
      }, 100);
      return () => clearTimeout(timeout);
    }
  }, [currentIndex]);

  const features = [
    {
      icon: <FaShieldAlt className="text-4xl" style={{ color: colors.primary }} />,
      title: "Ethical Finance Certified",
      description:
        "All products meet rigorous ethical finance standards with independent oversight for compliance assurance",
    },
    {
      icon: <FaHandHoldingUsd className="text-4xl" style={{ color: colors.primary }} />,
      title: "Profit-Sharing Model",
      description:
        "Collaborative financing structures replace interest charges, ensuring fair value exchange",
    },
    {
      icon: <FaBalanceScale className="text-4xl" style={{ color: colors.primary }} />,
      title: "Equitable Partnerships",
      description:
        "Mutually beneficial agreements that align success between institution and clients",
    },
    {
      icon: <FaChartLine className="text-4xl" style={{ color: colors.primary }} />,
      title: "Community Empowerment",
      description:
        "Driving local economic growth through responsible financial solutions",
    },
  ];

  const principles = [
    "Prohibition of Riba (interest)",
    "Asset-backed financing",
    "Risk-sharing partnerships",
    "Ethical investment screening",
    "Social responsibility",
  ];

  return (
    <div className="mt-30 mb-20 space-y-12">
      <MurabahahHeroSection />

      <div className="mt-6">
        <motion.h2
          initial={{ opacity: 1, y: 0 }}
          className="mb-12 text-center text-4xl font-bold sm:text-5xl"
          style={{ color: colors.primary }}
        >
          <span className="relative inline-block">
            <span className="bg-gradient-to-r bg-clip-text text-transparent" style={{ backgroundImage: `linear-gradient(to right, ${colors.primary}, ${colors.primaryLight})` }}>
              Lana Interest-Free Services
            </span>
            <span className="absolute bottom-0 left-0 h-1 w-full bg-gradient-to-r" style={{ background: `linear-gradient(to right, ${colors.primary}, ${colors.primaryLight})` }}></span>
          </span>
        </motion.h2>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="mx-auto mb-5 mt-5 max-w-[1500px] text-center font-poppins text-xl"
          style={{ color: colors.textDark }}
        >
          <span className="font-semibold" style={{ color: colors.primary }}>
            Yegna Microfinance
          </span>{" "}
          is on a mission to transform finance in
          <span className="font-medium" style={{ color: colors.primary }}>
            {" "}
            Ethiopia.
          </span>{" "}
          With Lana, our groundbreaking interest-free service,
          <span className="font-medium" style={{ color: colors.primary }}>
            {" "}
            with accessible capital—without the burden of interest.
          </span>
          Ethical, inclusive, and designed for progress. Welcome to the future of finance.
        </motion.div>
      </div>

      <div className="mt-20 px-4 lg:mx-auto lg:max-w-[1500px]" id="projects"></div>

      <section className="relative overflow-hidden py-16 md:py-24" style={{ backgroundColor: `${colors.primary}05` }}>
        <div className="absolute right-0 top-0 h-64 w-64 rounded-full opacity-30 blur-xl" style={{ backgroundColor: `${colors.primary}10` }}></div>
        <div className="absolute bottom-0 left-0 h-64 w-64 rounded-full opacity-30 blur-xl" style={{ backgroundColor: `${colors.primary}10` }}></div>

        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center gap-12 lg:flex-row">
            <div className="w-full lg:w-1/2">
              <div className="relative flex justify-center px-4 sm:px-0">
                <div className="relative h-[18rem] w-[18rem] sm:h-[22rem] sm:w-[22rem] md:h-[25rem] md:w-[25rem] lg:h-[28rem] lg:w-[28rem]">
                  <div className="absolute -left-6 -top-6 h-40 w-40 rounded-full blur-xl sm:-left-8 sm:-top-8 sm:h-48 sm:w-48 md:-left-10 md:-top-10 md:h-56 md:w-56 lg:-left-12 lg:-top-12 lg:h-64 lg:w-64 lg:blur-2xl" style={{ background: `radial-gradient(circle, ${colors.primary}20, ${colors.primaryLight}10)` }}></div>

                  <div className="sm:border-6 relative h-full w-full overflow-hidden rounded-full border-4 shadow-lg md:border-8 lg:shadow-[0_20px_50px_rgba(57,87,80,0.3)]" style={{ borderColor: colors.white }}>
                    <Image
                      src="/images/sheria/img78.jpg"
                      alt="Islamic Finance Principles"
                      width={448}
                      height={448}
                      className="contrast-105 h-full w-full object-cover brightness-90"
                      loading="lazy"
                    />
                    <div className="absolute inset-0" style={{ background: `radial-gradient(circle_at_30%_50%, transparent 45%, ${colors.primary}B3 45.5%)`, mixBlendMode: "multiply" }}></div>
                    <div className="absolute inset-0 opacity-70" style={{ background: `radial-gradient(circle_at_30%_50%, transparent 45%, white 45.5%)`, mixBlendMode: "screen" }}></div>
                  </div>

                  <div className="absolute -bottom-4 left-1/2 flex -translate-x-1/2 transform items-center space-x-1 rounded-full border bg-white px-3 py-1 shadow-md sm:-bottom-5 sm:space-x-2 sm:px-4 sm:py-1.5 md:-bottom-6 md:px-6 md:py-2 lg:shadow-[0_5px_15px_rgba(57,87,80,0.2)]" style={{ borderColor: colors.primaryLight }}>
                    <div className="relative h-4 w-4 sm:h-5 sm:w-5">
                      <div className="absolute h-full w-full rounded-full border sm:border-2" style={{ borderColor: colors.primary }}></div>
                      <div className="absolute left-[0.25rem] h-full w-full rounded-full sm:left-[0.35rem]" style={{ background: `linear-gradient(to bottom right, ${colors.primary}, ${colors.primaryLight})` }}></div>
                    </div>
                    <span className="text-xs font-semibold sm:text-sm md:text-base" style={{ color: colors.primary }}>
                      Sharia Compliant
                    </span>
                  </div>

                  <div className="absolute -right-4 top-1/4 h-10 w-10 rounded-full blur-md sm:-right-6 sm:h-12 sm:w-12 md:-right-8 md:h-16 md:w-16 lg:blur-lg" style={{ background: `radial-gradient(circle, ${colors.primary}10, ${colors.primaryLight}05)` }}></div>
                  <div className="absolute bottom-6 left-6 h-8 w-8 rounded-full blur-sm sm:bottom-8 sm:left-8 sm:h-10 sm:w-10 md:blur-md" style={{ background: `radial-gradient(circle, ${colors.primary}10, ${colors.primaryLight}10)` }}></div>

                  <div className="pointer-events-none absolute -inset-2 rounded-full border sm:-inset-3 sm:border-2 md:-inset-4" style={{ borderColor: `${colors.primary}20` }}></div>
                </div>
              </div>
            </div>

            <div className="w-full lg:w-1/2">
              <div className="mb-4 flex items-center sm:mb-6">
                <span className="mr-2 text-xl sm:mr-3 sm:text-2xl">💼</span>
                <span className="rounded-full px-3 py-1 text-xs font-medium sm:px-4 sm:py-2 sm:text-sm" style={{ backgroundColor: `${colors.primary}10`, color: colors.primary }}>
                  Ethical microfinance Services
                </span>
              </div>

              <h2 className="mb-4 text-2xl font-bold sm:mb-6 sm:text-3xl md:text-4xl" style={{ color: colors.textDark }}>
                <span style={{ color: colors.primary }}>Lana Interest Free Service</span> — Your Trusted
                Partner in interest-free Finance
              </h2>

              <p className="mb-4 text-base leading-relaxed sm:mb-6 sm:text-lg md:mb-8" style={{ color: colors.textDark }}>
                Committed to ethical financial practices, we provide innovative interest-free
                solutions that meet modern economic needs through transparent and fair services.
              </p>

              <div className="grid grid-cols-2 gap-3 sm:gap-4 md:gap-6">
                {[
                  { title: "Murabaha", desc: "Cost-plus financing" },
                  { title: "Ijarah", desc: "Leasing agreements" },
                  { title: "Musharakah", desc: "Profit-sharing" },
                  { title: "Wadia Saving", desc: "Safe custody accounts" },
                ].map((item, index) => (
                  <div
                    key={index}
                    className="shadow-xs rounded-lg border p-2 sm:rounded-xl sm:p-3 sm:shadow-sm md:p-4"
                    style={{ borderColor: `${colors.primary}10`, backgroundColor: colors.white }}
                  >
                    <div className="flex items-start">
                      <div>
                        <h3 className="text-sm font-medium sm:text-base" style={{ color: colors.textDark }}>
                          {item.title}
                        </h3>
                        <p className="text-xs" style={{ color: colors.textDark }}>
                          {item.desc}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-16 md:py-24" style={{ backgroundColor: colors.white }}>
        <div className="container mx-auto px-4">
          <div className="mx-auto mb-16 max-w-4xl text-center">
            <h2 className="text-3xl font-bold md:text-4xl" style={{ color: colors.textDark }}>
              Our <span style={{ color: colors.primary }}>Sharia-Compliant</span> Products
            </h2>
            <div className="mx-auto mt-4 h-1 w-24" style={{ background: `linear-gradient(to right, ${colors.primary}, ${colors.primaryLight})` }}></div>
            <p className="mt-6 text-lg leading-relaxed" style={{ color: colors.textDark }}>
              Financial solutions designed to grow your wealth while remaining fully aligned with Sharia principles
            </p>
          </div>

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {[
              {
                title: "Investment Accounts",
                items: ["Mudaraba Investment", "Musharakah", "Salam"],
                icon: "📊",
              },
              {
                title: "Savings Plans",
                items: ["Wadia Saving", "Mudaraba Saving", "Children Saving"],
                icon: "💰",
              },
              {
                title: "Special Programs",
                items: ["Women Saving", "Qard Al Hassen", "Istisna"],
                icon: "🌟",
              },
            ].map((category, index) => (
              <div
                key={index}
                className="overflow-hidden rounded-xl border"
                style={{ borderColor: `${colors.primary}10`, backgroundColor: `${colors.primary}05` }}
              >
                <div className="p-6 text-white" style={{ background: `linear-gradient(135deg, ${colors.primary}, ${colors.primaryDark})` }}>
                  <div className="flex items-center">
                    <span className="mr-3 text-3xl">{category.icon}</span>
                    <h3 className="text-xl font-semibold">{category.title}</h3>
                  </div>
                </div>
                <div className="p-6">
                  <ul className="space-y-3">
                    {category.items.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-start">
                        <svg
                          className="mr-2 mt-0.5 h-5 w-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          style={{ color: colors.primary }}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        <span style={{ color: colors.textDark }}>{item}</span>
                      </li>
                    ))}
                  </ul>
                  <button className="mt-6 w-full rounded-lg border px-4 py-2 font-medium transition-colors" style={{ borderColor: colors.primary, color: colors.primary }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = colors.primary;
                      e.currentTarget.style.color = colors.white;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "transparent";
                      e.currentTarget.style.color = colors.primary;
                    }}>
                    Learn More
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 md:py-24" style={{ backgroundColor: `${colors.primary}05` }}>
        <div className="container mx-auto px-4">
          <div className="mx-auto mb-16 max-w-4xl text-center">
            <h2 className="text-3xl font-bold md:text-4xl" style={{ color: colors.textDark }}>
              The <span style={{ color: colors.primary }}>Principles</span> of Interest-free
              Microfinance
            </h2>
            <div className="mx-auto mt-4 h-1 w-24" style={{ background: `linear-gradient(to right, ${colors.primary}, ${colors.primaryLight})` }}></div>
            <p className="mt-6 text-lg" style={{ color: colors.textDark }}>
              Our Microfinance model is built on four foundational pillars of
              Sharia-compliant finance
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-4">
            {[
              { step: "1", title: "Interest-Free", description: "No riba (interest) is charged or paid in any transactions", icon: "🚫" },
              { step: "2", title: "Asset-Backed", description: "All financing must be tied to tangible assets or services", icon: "🏢" },
              { step: "3", title: "Risk-Sharing", description: "Profits and losses are shared between parties", icon: "⚖️" },
              { step: "4", title: "Ethical Investments", description: "Funds only go to halal (permissible) businesses", icon: "🌱" },
            ].map((item, index) => (
              <div
                key={index}
                className="rounded-xl border p-8 transition-all hover:shadow-lg"
                style={{ borderColor: `${colors.primary}20`, backgroundColor: `${colors.primary}03` }}
              >
                <div className="mb-4 flex items-center">
                  <div className="mr-4 flex h-12 w-12 items-center justify-center rounded-full border-2 text-xl" style={{ borderColor: colors.primary, backgroundColor: colors.white }}>
                    {item.icon}
                  </div>
                  <div className="flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold text-white" style={{ backgroundColor: colors.primary }}>
                    {item.step}
                  </div>
                </div>
                <h3 className="mb-3 text-xl font-semibold" style={{ color: colors.primary }}>
                  {item.title}
                </h3>
                <p style={{ color: colors.textDark }}>{item.description}</p>
              </div>
            ))}
          </div>

          <div className="mt-20 flex justify-center">
            <div className="w-full max-w-4xl overflow-hidden rounded-xl border shadow-sm" style={{ borderColor: `${colors.primary}10`, backgroundColor: colors.white }}>
              <div className="grid divide-x md:grid-cols-2" style={{ borderColor: `${colors.primary}10` }}>
                <div className="p-8 md:p-10">
                  <div className="mb-6 flex items-center">
                    <div className="mr-4 flex h-10 w-10 items-center justify-center rounded-full text-lg text-white" style={{ backgroundColor: colors.primary }}>
                      ⚖️
                    </div>
                    <h3 className="text-xl font-bold" style={{ color: colors.primary }}>
                      Interest-free Microfinance
                    </h3>
                  </div>
                  <ul className="space-y-4">
                    <li className="flex items-start">
                      <svg className="mr-2 mt-0.5 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ color: colors.primary }}>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Profit-loss sharing model</span>
                    </li>
                    <li className="flex items-start">
                      <svg className="mr-2 mt-0.5 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ color: colors.primary }}>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Asset-backed transactions</span>
                    </li>
                    <li className="flex items-start">
                      <svg className="mr-2 mt-0.5 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ color: colors.primary }}>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Ethical investment screening</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sharia Oversight Section */}
      <section className="relative overflow-hidden py-24 text-white" style={{ backgroundColor: colors.primary }}>
        <div className="absolute -left-20 -top-20 h-48 w-48 rounded-full opacity-10" style={{ backgroundColor: colors.primaryDark }}></div>
        <div className="absolute -left-10 -top-10 h-32 w-32 rounded-full opacity-70" style={{ backgroundColor: colors.white }}></div>

        <div className="container relative z-10 mx-auto px-4">
          <div className="flex flex-col items-center gap-16 lg:flex-row">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:w-1/2 p-6 rounded-lg"
              style={{ backgroundColor: `${colors.white}10` }}
            >
              <h2 className="mb-6 text-4xl font-extrabold">
                <span style={{ color: colors.primaryLight }}>Sharia Oversight</span>
              </h2>
              <p className="mb-8 text-lg leading-relaxed text-white">
                Our Sharia Advisory Board ensures every financial product follows
                Sharia guidelines as faithfully as the moon follows its celestial path.
              </p>

              <div className="space-y-6">
                {[
                  "Product Certification & Approval",
                  "Annual Sharia Compliance Audits",
                  "Continuous Transaction Monitoring",
                  "Fatwa Issuance & Guidance",
                  "Staff Training & Education",
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-start"
                  >
                    <span className="mr-3 mt-1" style={{ color: colors.primaryLight }}>✅</span>
                    <span className="text-lg text-white">{item}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="lg:w-1/2"
            >
              <div className="relative aspect-video overflow-hidden rounded-xl border-4" style={{ borderColor: `${colors.primaryLight}30` }}>
                <Image
                  src="/images/sheria/sheck.jpg"
                  alt="Sharia Board Meeting"
                  width={800}
                  height={600}
                  className="h-full w-full object-cover"
                />
                <div className="absolute inset-0 flex items-end p-8" style={{ background: `linear-gradient(to top, ${colors.primary}E6, transparent)` }}>
                  <div>
                    <h3 className="text-2xl font-bold text-white">
                      Sharia Advisory Board
                    </h3>
                    <p className="mt-2 text-white">
                      Internationally recognized Sharia-compliant finance scholars
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 text-white" style={{ background: `linear-gradient(135deg, ${colors.primaryDark}, ${colors.primary})` }}>
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="mb-8 flex justify-center"
          >
            <div className="relative h-24 w-24">
              <div className="absolute inset-0 flex items-center justify-center rounded-full text-4xl" style={{ backgroundColor: `${colors.primaryLight}20` }}>
                ⚖️
              </div>
            </div>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="mb-6 text-4xl font-bold text-white"
          >
            Begin Your Ethical Financial Journey
          </motion.h2>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="mx-auto mb-8 max-w-2xl text-xl text-white"
          >
            Let Lana IF guide you through the phases of financial growth while
            staying true to Sharia-compliant values.
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="flex flex-col justify-center gap-4 sm:flex-row"
          >
            <button className="rounded-lg px-8 py-3 font-medium transition" style={{ backgroundColor: colors.white, color: colors.primary }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = colors.primaryLight; e.currentTarget.style.color = colors.white; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = colors.white; e.currentTarget.style.color = colors.primary; }}>
              Open an Account
            </button>
            <button className="rounded-lg border-2 px-8 py-3 font-medium transition" style={{ borderColor: colors.white, color: colors.white }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = colors.white; e.currentTarget.style.color = colors.primary; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = colors.white; }}>
              Contact Us
            </button>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.6 }}
            className="mt-6 text-lg font-semibold text-white"
          >
            📞 +251965444449 / +2519833333
          </motion.p>
        </div>
      </section>
    </div>
  );
};

export default AboutLanaIF;